import classnames from 'classnames';
import { stripeElementsRow } from './payment-methods/PaymentMethodOption';

interface Props {
  /** How many method rows to draw. */
  rows: number;
  /** The wallet buttons and the "or" divider above the rows (the Express Checkout Element). */
  withWallets?: boolean;
  /** How many wallet buttons, two to a row as the Express Checkout Element lays them out. */
  wallets?: number;
  /** The collapsed "more payment methods" heading below the rows. */
  withMoreMethods?: boolean;
  testId?: string;
  className?: string;
}

/**
 * The payment section while its methods load: the rows' own card (border, radius, padding) with a
 * radio, a label and a logo drawn as pulsing bars, so the section keeps its shape instead of
 * standing empty. Bars are a step darker than the card and the page, or they vanish into them; the
 * cards themselves stay solid, so only the content pulses.
 */
export default function PaymentMethodsSkeleton({
  rows,
  withWallets = false,
  wallets = 2,
  withMoreMethods = false,
  testId = 'payment-methods-skeleton',
  className,
}: Props) {
  return (
    <div className={classnames('flex flex-col gap-sm', className)} data-test={testId} aria-hidden>
      {withWallets && (
        <>
          <div className="grid grid-cols-2 gap-sm">
            {Array.from({ length: Math.max(wallets, 1) }, (_, index) => (
              <div
                className={classnames(walletButton, { 'col-span-2': wallets === 1 })}
                data-skeleton="wallet"
                key={`payment-methods-skeleton-wallet-${index}`}
              />
            ))}
          </div>
          <div className={divider}>
            <span className={dividerLine} />
            <span className={dividerLabel} />
            <span className={dividerLine} />
          </div>
        </>
      )}

      {Array.from({ length: rows }, (_, index) => (
        <div
          className={classnames(stripeElementsRow, '!cursor-default')}
          data-skeleton="row"
          key={`payment-methods-skeleton-row-${index}`}
        >
          <div className="flex items-center gap-sm h-5">
            <span className={radio} />
            <span className={classnames(bar, index % 2 === 0 ? 'w-28' : 'w-20')} />
            <span className={classnames(bar, 'ml-auto w-8 h-4')} />
          </div>
        </div>
      ))}

      {withMoreMethods && <span className={classnames(bar, 'mt-sm w-44')} data-skeleton="more-methods" />}
    </div>
  );
}

const pulse = 'animate-pulse bg-gray dark:bg-black-100';
const bar = classnames('block h-3 rounded-full', pulse);
const radio = 'block h-4 w-4 shrink-0 rounded-full border-[1.5px] border-gray-1000 dark:border-brand-border';
const walletButton = classnames('h-[48px] rounded-[8px]', pulse);
const divider = 'flex items-center gap-sm pt-sm';
const dividerLine = 'h-px flex-1 bg-gray dark:bg-brand-border';
const dividerLabel = classnames('block h-2 w-5 rounded-full', pulse);
